import {
  Button,
  Backdrop,
  Box,
  Fade,
  Modal,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import { GCM } from '../../../assets/utils/cipher';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import {
  variantTextButton,
  gradientBorder,
} from '../../../assets/styles/button.style';
import { axiosInstance } from '../../../assets/utils/axiosInstance';
import getErrorMessage from '../../../assets/utils/getErrorMessage';
import ErrorResponseObject from '../../../interfaces/errorResponseObject';

interface FeedbackModalProps {
  open: boolean;
  handleClose: () => void;
  handleClickSuccess: () => void;
}

const SubmitFeedbackForm = ({
  open,
  handleClose,
  handleClickSuccess,
}: FeedbackModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const { createToastNotification } = useToastNotificationContext();
  const { reportDetailsId } = useParams();

  const formik = useFormik({
    initialValues: {
      reasons: '',
      submit: null,
    },
    validationSchema: Yup.object({
      reasons: Yup.string().required('Required reason.'),
    }),
    onSubmit: async (values, helpers) => {
      setIsLoading(true);
      try {
        const gcm = new GCM();
        const cipherDescriptionText = gcm.encrypt({
          plainText: values.reasons,
        });

        const body = {
          report: reportDetailsId,
          content: cipherDescriptionText,
        };

        await axiosInstance().post('feedback', body);

        handleClose();
        handleClickSuccess();
        setIsLoading(false);
      } catch (err) {
        helpers.setStatus({ success: false });
        // add this to the interface folder
        const customError = getErrorMessage(err as ErrorResponseObject);
        createToastNotification(customError, 'error');

        helpers.setErrors({ submit: customError });
        helpers.setSubmitting(false);
        setIsLoading(false);
      }
    },
  });

  return (
    <Modal
      aria-labelledby='transition-modal-title'
      aria-describedby='transition-modal-description'
      open={open}
      //   onClose={handleClose}  // on exit
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            left: '50%',
            transform: 'translateX(-50%)',
            flexDirection: 'column',
            borderRadius: '12px',
            position: 'fixed',
            top: '243.5px',
            width: '600px',
            padding: '32px 28px',
            gap: '48px',
            bgcolor: '#FFFFFF',
            boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.16)',
          }}
        >
          <form noValidate onSubmit={formik.handleSubmit}>
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: '22.5px',
                color: '#181818',
                mt: '1rem',
                mb: '30px',
              }}
            >
              Thank you for your report, which we've carefully reviewed and
              concluded the investigation.
            </Typography>
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Schibsted Grotesk',
                fontSize: '.8rem',
                fontWeight: 500,
                lineHeight: '17px',
                letterSpacing: '0em',
                textAlign: 'left',
                mt: '1rem',
                mb: '0.5rem',
              }}
            >
              We invite you to share feedback.
            </Typography>

            <TextField
              multiline
              rows={11}
              id='filled-textarea'
              placeholder='Kindly enter your feedback'
              variant='filled'
              fullWidth
              autoComplete='off'
              type='text'
              error={!!(formik.touched.reasons && formik.errors.reasons)}
              helperText={formik.touched.reasons && formik.errors.reasons}
              name='reasons'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.reasons}
              sx={{
                opacity: isLoading ? 0.7 : 100,
                pointerEvents: isLoading ? 'none' : 'auto',
                ...{
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    borderRadius: '6px',
                    padding: '20px',
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '17.28px',
                    color: '#555555',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  mb: '1rem',
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                },
              }}
            />

            {/* Buttons */}
            <Box sx={{ width: '100%' }}>
              {/* Cancel Button */}
              <Button
                variant='text'
                onClick={handleClose}
                // disabled={isLoading}
                sx={{
                  ...variantTextButton,
                  width: 'calc(50% - 0.2rem)',
                  margin: '1rem .1rem .1rem 0',
                }}
              >
                Cancel
              </Button>

              {/* Submit Button */}
              <Button
                variant='outlined'
                type='submit'
                // disabled={isLoading}
                sx={{
                  ...gradientBorder,
                  color: 'black',
                  width: 'calc(50% - 0.2rem)',
                  margin: '1rem .1rem .1rem 0',
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Submit'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SubmitFeedbackForm;
